import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { format, startOfDay, subDays } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type PerkDatePresetId = "today" | "yesterday" | "last4" | "last30";

const PRESETS: { id: PerkDatePresetId; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last4", label: "Last 4 days" },
  { id: "last30", label: "Last 30 days" },
];

export function perkDatePresetRange(id: PerkDatePresetId): DateRange {
  const today = startOfDay(new Date());
  if (id === "today") return { from: today, to: today };
  if (id === "yesterday") {
    const yesterday = startOfDay(subDays(today, 1));
    return { from: yesterday, to: yesterday };
  }
  if (id === "last4") {
    return { from: startOfDay(subDays(today, 3)), to: today };
  }
  return { from: startOfDay(subDays(today, 29)), to: today };
}

function sameDay(left?: Date, right?: Date) {
  if (!left || !right) return false;
  return startOfDay(left).getTime() === startOfDay(right).getTime();
}

const perkDay =
  "!bg-[#1B7A45] !text-white hover:!bg-[#166534] hover:!text-white focus:!bg-[#1B7A45] focus:!text-white";

/** Gray band only on days inside the range. Endpoints stay solid green with white numbers. */
const perkCalendarClassNames = {
  cell: "relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected='true'])]:bg-accent [&:has([aria-selected='true'].day-outside)]:bg-accent/50 [&:has([aria-selected='true'].day-range-start)]:rounded-l-md [&:has([aria-selected='true'].day-range-end)]:rounded-r-md first:[&:has([aria-selected='true'])]:rounded-l-md last:[&:has([aria-selected='true'])]:rounded-r-md",
  day_selected: "",
  day_range_start: `day-range-start ${perkDay}`,
  day_range_end: `day-range-end ${perkDay}`,
  day_range_middle: "!bg-transparent !text-slate-900 hover:!bg-transparent hover:!text-slate-900",
  day_today: "",
  day_outside: "day-outside text-muted-foreground",
};

const perkRangeStyles = {
  range_start: { backgroundColor: "#1B7A45", color: "#ffffff" },
  range_end: { backgroundColor: "#1B7A45", color: "#ffffff" },
};

function matchingPreset(date?: DateRange): PerkDatePresetId | "custom" | null {
  if (!date?.from || !date.to) return null;
  const match = PRESETS.find((preset) => {
    const range = perkDatePresetRange(preset.id);
    return sameDay(range.from, date.from) && sameDay(range.to, date.to);
  });
  return match?.id ?? "custom";
}

interface DatePickerWithRangeProps {
  date?: DateRange;
  setDate: (date: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  /** Perk Point pickers open on Today, Yesterday, Last 4 days, Last 30 days, then a custom calendar. */
  presets?: boolean;
}

export function DatePickerWithRange({
  date,
  setDate,
  placeholder = "Pick a date range",
  className,
  presets = false,
}: DatePickerWithRangeProps) {
  const [open, setOpen] = React.useState(false);
  const [showCalendar, setShowCalendar] = React.useState(false);
  const selected = presets ? matchingPreset(date) : null;
  const presetLabel =
    selected && selected !== "custom"
      ? PRESETS.find((preset) => preset.id === selected)?.label
      : null;

  const handleSelect = (range: DateRange | undefined) => {
    setDate(range);
    if (range?.from && range?.to) {
      setTimeout(() => {
        setOpen(false);
        setShowCalendar(false);
      }, 100);
    }
  };

  const choosePreset = (id: PerkDatePresetId) => {
    setDate(perkDatePresetRange(id));
    setShowCalendar(false);
    setOpen(false);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) setShowCalendar(false);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {presetLabel ? (
              presetLabel
            ) : date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          {presets ? (
            <div className="flex flex-col sm:flex-row">
              <div className="flex min-w-[180px] flex-col gap-1 p-2">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className={cn(
                      "rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100",
                      selected === preset.id &&
                        "bg-emerald-50 font-medium text-emerald-900",
                    )}
                    onClick={() => choosePreset(preset.id)}
                  >
                    {preset.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={cn(
                    "rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100",
                    (showCalendar || selected === "custom") &&
                      "bg-emerald-50 font-medium text-emerald-900",
                  )}
                  onClick={() => setShowCalendar(true)}
                >
                  Custom range
                </button>
              </div>
              {showCalendar ? (
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={date?.from}
                  selected={date}
                  onSelect={handleSelect}
                  numberOfMonths={2}
                  classNames={perkCalendarClassNames}
                  modifiersStyles={perkRangeStyles}
                />
              ) : null}
            </div>
          ) : (
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={handleSelect}
              numberOfMonths={2}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
